/**
 * Plays a Mac's computer sound in live view (#294). The Mac app can't put it in the "audio" media slot (its WebRTC
 * build only sends the microphone), so it streams 16 kHz mono 16-bit PCM chunks over a data channel; this schedules
 * them back to back through Web Audio, with a short cushion against network jitter. Chunks that arrive so late that
 * the sound would fall behind the picture are dropped.
 */
export class PcmPlayer {
  private ctx: AudioContext;
  private gain: GainNode;
  private next = 0;

  constructor(private readonly rate = 16_000) {
    this.ctx = new AudioContext();
    this.gain = this.ctx.createGain();
    this.gain.connect(this.ctx.destination);
  }

  /** Mute or unmute (the "hear computer sound" toggle). */
  setMuted(muted: boolean) {
    this.gain.gain.value = muted ? 0 : 1;
    if (!muted && this.ctx.state === 'suspended') void this.ctx.resume();
  }

  /** One chunk of little-endian 16-bit PCM. */
  push(data: ArrayBuffer) {
    const samples = new Int16Array(data);
    if (!samples.length) return;
    const now = this.ctx.currentTime;
    // More than ~0.6 s queued: we're behind, so skip this chunk rather than drift further from the picture.
    if (this.next - now > 0.6) return;
    const buffer = this.ctx.createBuffer(1, samples.length, this.rate);
    const ch = buffer.getChannelData(0);
    for (let i = 0; i < samples.length; i++) ch[i] = samples[i]! / 32768;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.gain);
    // Start a little ahead the first time (or after a gap) so small jitter doesn't cause clicks.
    if (this.next < now + 0.02) this.next = now + 0.15;
    src.start(this.next);
    this.next += buffer.duration;
  }

  close() {
    void this.ctx.close().catch(() => undefined);
  }
}
